import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // Intentionally no console logging here. Production apps should route this to telemetry.
  }

  private handleReload = () => {
    globalThis.location.reload();
  };

  private handleTryAgain = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-[60vh] w-full items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg rounded-xl border border-border-default/60 bg-action-primary/5 p-6 backdrop-blur-xl">
          <div className="text-lg font-semibold text-primary">Something went wrong</div>
          <p className="mt-2 text-sm text-secondary">
            The app hit an unexpected error. You can try again or reload the page.
          </p>

          {this.state.error?.message ? (
            <div className="mt-4 rounded-md border border-border-default/50 bg-surface/50 p-3 text-xs text-secondary">
              {this.state.error.message}
            </div>
          ) : null}

          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={this.handleTryAgain}
              className="rounded-md border border-border-default/60 px-3 py-2 text-sm text-primary transition-opacity hover:opacity-90"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-md bg-action-primary px-3 py-2 text-sm text-white transition-opacity hover:opacity-90"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}

